import { useIsFocused } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import { newListingSourceAtom } from 'app/atoms';
import { type ReviewSwipeAction, type SwipeableReviewCardHandle } from 'app/components/swipeable-review-card';
import { useNavigationOptions } from 'app/hooks/navigation';
import { Event, track } from 'app/lib/analytics';
import { getI18nLanguage, useI18nLanguage, type Language } from 'app/lib/language';
import { useStatistics } from 'app/network/statistics';
import { getSuggestedListingsWithCount } from 'app/network/suggested-listing';
import { useUser } from 'app/network/user';
import { canUseCentralizedCreation, canUseOfferHub } from 'app/screens/intro/navigator-helper';
import { useSocialFeedFeature } from 'app/hooks/use-social-feed-feature';
import { useTheme } from 'app/shopify';
import { ListingType } from 'app/types/listing';
import type { NavigationScreen } from 'app/types/navigation';
import { usePageTitle } from 'app/utils/web-helpers';
import { DynamicFeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { Image as ExpoImage } from 'expo-image';
import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityInfo, Platform } from 'react-native';
import HapticFeedback from 'react-native-haptic-feedback';
import { useReducedMotion } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { v4 as uuid } from 'uuid';
import { type HubTab, type HubTutorialStep } from './components';
import { useOfferHubQueue } from './queue-provider';
import { completedListCount, refreshHubRound, remainingRoundItems, useHubSession, type HubRound } from './session';

import type { ComponentProps } from 'react';

export function useOfferHubState({ navigation, route }: ComponentProps<NavigationScreen<'offer-hub' | 'publish'>>) {
  const { t } = useTranslation('offer-hub');
  const language = useI18nLanguage();
  const [roundLanguage, setRoundLanguage] = useState<string>();
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const focused = useIsFocused();
  const reducedMotion = useReducedMotion();
  const storedCreationSource = useAtomValue(newListingSourceAtom);
  const [creationSource] = useState(() => route.params?.newListingSource ?? storedCreationSource);
  const resetNewListingSource = useResetAtom(newListingSourceAtom);
  const {
    userQuery,
    user,
    centralizedCreation,
    canUseSuggestions,
    hasAccess,
    session,
    queue,
    ready,
    decisions,
    storageError,
    candidates,
  } = useOfferHubData(route.name, language);
  const { save } = session;
  const { canUseSocialFeed } = useSocialFeedFeature();
  const canUseStories = centralizedCreation && canUseSocialFeed;
  const [tutorialStep, setTutorialStep] = useState<HubTutorialStep>(0);
  const [replayingTutorial, setReplayingTutorial] = useState(false);
  const [swipeTutorialStep, setSwipeTutorialStep] = useState(0);
  const [replayingSwipeTutorial, setReplayingSwipeTutorial] = useState(false);
  const [active, setActive] = useState(false);
  const { round, setRound, items, currentItem, completed, inProgress, accepted, pending, failed } =
    useOfferHubRoundState(active, decisions);
  const [fetchingRound, setFetchingRound] = useState(false);
  const [roundError, setRoundError] = useState(false);
  const swipeTutorialCard = useRef<SwipeableReviewCardHandle>(null);
  const manualRoute = {
    key: `${route.key}:manual`,
    name: 'new' as const,
    params: {
      ...route.params,
      newListingSource: creationSource,
      defaultValues: { ...route.params?.defaultValues, type: route.params?.defaultValues?.type ?? ListingType.offer },
    },
  };
  const bulkRoute = {
    key: `${route.key}:photos`,
    name: 'bulkNew' as const,
    params: { newListingSource: creationSource },
  };
  const swipeCard = useRef<SwipeableReviewCardHandle>(null);
  const committed = useRef(new Set<string>());
  const starting = useRef(false);
  const initialQueueErrorsCleared = useRef(false);
  const canUseAI = Boolean(user && isFeatureEnabled(DynamicFeatureFlag.BulkListingCreation, user.featureFlags));
  const { tab, setTab, visitedTabs, selectTab } = useOfferHubTabs({
    route,
    canUseSuggestions,
    canUseAI,
    canUseStories,
    isUserPending: userQuery.isPending,
  });
  const firstPreviewPicture = items[0]?.listingGroup.picture;
  const secondPreviewPicture = items[1]?.listingGroup.picture;
  const thirdPreviewPicture = items[2]?.listingGroup.picture;
  const tutorial = canUseSuggestions && (!session.tutorialCompleted || replayingTutorial);
  const { swiping, swipeTutorialSwiping } = getOfferHubInteraction({
    tab,
    active,
    completed,
    hasCurrentItem: Boolean(currentItem),
    tutorial,
    replayingSwipeTutorial,
    swipeTutorialStep,
  });
  const statistics = useStatistics(user?.id ?? '', { enabled: hasAccess && completed });
  const { refetch: refreshStatistics } = statistics;

  const setPageTitle = usePageTitle('offer-hub', navigation);
  useEffect(() => setPageTitle('title'), [setPageTitle]);
  useNavigationOptions(navigation, { headerShown: false });
  useEffect(() => () => resetNewListingSource(), [resetNewListingSource]);
  useEffect(() => {
    if (!focused || !ready || !queue || initialQueueErrorsCleared.current) return;
    initialQueueErrorsCleared.current = true;
    queue.clearErrors();
  }, [focused, queue, ready]);
  useEffect(() => {
    if (userQuery.isPending || hasAccess) return;
    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate('main', { screen: 'billboard' });
  }, [hasAccess, navigation, userQuery.isPending]);
  useInitializeHubRound({
    round,
    setRound,
    ready,
    session,
    candidates,
    decisions,
    language,
    roundLanguage,
    setRoundLanguage,
  });
  useOfferHubFocusReset(focused, () => {
    setActive(false);
    setTutorialStep(0);
    setReplayingTutorial(false);
    setSwipeTutorialStep(0);
    setReplayingSwipeTutorial(false);
    if (round && !remainingRoundItems(round, decisions).length) setRound(null);
  });
  useEffect(() => {
    const urls = [firstPreviewPicture, secondPreviewPicture, thirdPreviewPicture].filter((url): url is string =>
      Boolean(url),
    );
    if (urls.length) void ExpoImage.prefetch(urls).catch(() => undefined);
  }, [firstPreviewPicture, secondPreviewPicture, thirdPreviewPicture]);
  useEffect(() => {
    if (!completed) return;
    AccessibilityInfo.announceForAccessibility(t('listComplete'));
    if (Platform.OS !== 'web' && !reducedMotion) HapticFeedback.trigger('notificationSuccess');
  }, [completed, reducedMotion, t]);
  useEffect(() => {
    if (completed) void refreshStatistics();
  }, [accepted.length, completed, refreshStatistics]);

  const close = () => {
    if (route.name === 'publish') {
      navigation.navigate('main', { screen: 'billboard' });
      return;
    }
    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate('main', { screen: 'billboard' });
  };
  const replayTutorial = () => {
    setTab('suggestions');
    setReplayingTutorial(true);
    setTutorialStep(0);
    setActive(false);
  };
  const replaySwipeTutorial = () => {
    setSwipeTutorialStep(0);
    setReplayingSwipeTutorial(true);
    setActive(false);
  };
  const dismissTutorial = () => {
    setTutorialStep(0);
    setReplayingTutorial(false);
    if (!session.tutorialCompleted) void save({ tutorialCompleted: true });
  };
  const decide = (kind: ReviewSwipeAction) => {
    if (!currentItem || !round || roundLanguage !== language || !queue || committed.current.has(currentItem.id)) return;
    const title = currentItem.listingGroup.name ?? '';
    if (
      !queue.enqueue({
        id: uuid(),
        roundId: round.id,
        suggestedListingId: currentItem.id,
        title,
        picture: currentItem.listingGroup.picture ?? '',
        kind,
        source: creationSource,
        status: 'pending',
        attempts: 0,
      })
    )
      return;
    committed.current.add(currentItem.id);
    if (Platform.OS !== 'web' && !reducedMotion) HapticFeedback.trigger('impactLight');
    // Also re-render synchronously when the outbox consumer has not notified React yet.
    setRound({ ...round });
  };
  const startList = async (fetchMore = false) => {
    if (starting.current) return;
    starting.current = true;
    setRoundError(false);
    const start = async () => {
      // Recheck campaign activation/expiry before displaying a saved round.
      setFetchingRound(true);
      await queue?.whenSettled();
      const result = await candidates.refetch();
      if (language !== getI18nLanguage()) return;
      if (result.isError || !result.data) {
        setRoundError(true);
        return;
      }
      const next = refreshHubRound(
        fetchMore || !currentItem || !round ? { id: uuid(), items: [] } : round,
        result.data.suggestions,
        queue?.getSnapshot().decisions ?? decisions,
        result.data.answeredCount,
      );
      if (!(await save({ round: next }))) return;
      if (language !== getI18nLanguage()) return;
      setRound(next);
      setRoundLanguage(language);
      setReplayingTutorial(false);
      setTutorialStep(0);
      setReplayingSwipeTutorial(false);
      setSwipeTutorialStep(0);
      setActive(true);
    };
    try {
      await start();
    } catch {
      setRoundError(true);
    }
    starting.current = false;
    setFetchingRound(false);
  };
  const tutorialVisible = tutorial;
  const loaded = ready && session.ready && Boolean(round) && roundLanguage === language;
  const { helpTutorialVisible, tutorialOverlayVisible, overviewActive } = getHubPresentationState({
    tab,
    focused,
    tutorialVisible,
    replayingSwipeTutorial,
    loaded,
  });
  const showError = storageError || session.storageError;

  return {
    userQuery,
    hasAccess,
    insets: route.name === 'publish' ? { ...insets, bottom: 0 } : insets,
    tab,
    tutorialOverlayVisible,
    tutorialStep,
    canUseAI,
    canUseStories,
    canUseSuggestions,
    centralizedCreation,
    setTutorialStep,
    dismissTutorial,
    visitedTabs,
    focused,
    bulkRoute,
    manualRoute,
    creationRequest: route.params,
    navigation,
    setActive,
    setTab,
    showError,
    candidates,
    roundError,
    failed,
    queue,
    swipeTutorialStep,
    swipeTutorialCard,
    t,
    setSwipeTutorialStep,
    currentItem,
    fetchingRound,
    startList,
    accepted,
    statistics,
    refreshStatistics,
    pending,
    helpTutorialVisible,
    swiping,
    replayTutorial,
    items,
    round,
    selectTab,
    replaySwipeTutorial,
    close,
    session,
    tutorialVisible,
    swipeTutorialSwiping,
    loaded,
    replayingSwipeTutorial,
    completed,
    swipeCard,
    decide,
    theme,
    inProgress,
    overviewActive,
    completedLists: completedListCount(round, decisions),
  };
}

export type OfferHubState = ReturnType<typeof useOfferHubState>;

function useOfferHubData(routeName: 'offer-hub' | 'publish', language: Language) {
  const userQuery = useUser();
  const user = userQuery.data;
  const centralizedCreation = canUseCentralizedCreation(user?.featureFlags, userQuery.isPending);
  const canUseSuggestions = canUseOfferHub(user?.featureFlags, userQuery.isPending);
  const hasAccess = routeName === 'publish' ? centralizedCreation : centralizedCreation || canUseSuggestions;
  const session = useHubSession(hasAccess ? user?.id : undefined);
  const { queue, ready, decisions, storageError } = useOfferHubQueue();
  const candidates = useQuery({
    queryKey: ['offer-hub', 'suggested-listings', user?.id, language],
    queryFn: async () => {
      await queue?.whenSettled();
      return getSuggestedListingsWithCount();
    },
    enabled: Boolean(user?.id) && hasAccess && canUseSuggestions && ready && Boolean(queue),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  return {
    userQuery,
    user,
    centralizedCreation,
    canUseSuggestions,
    hasAccess,
    session,
    queue,
    ready,
    decisions,
    storageError,
    candidates,
  };
}

function useOfferHubRoundState(active: boolean, decisions: ReturnType<typeof useOfferHubQueue>['decisions']) {
  const [round, setRound] = useState<HubRound | null>(null);
  const items = round ? remainingRoundItems(round, decisions) : [];
  const currentItem = items[0];
  const completed = Boolean(active && round?.items.length && !currentItem);
  const inProgress = Boolean(round && currentItem && items.length < round.items.length);
  const roundDecisions = decisions.filter((decision) => decision.roundId === round?.id);
  const accepted = roundDecisions.filter((decision) => decision.kind === 'accept' && decision.status === 'saved');
  const pending = roundDecisions.filter((decision) => decision.status === 'pending').length;
  const failed = decisions.filter((decision) => decision.status === 'failed');
  return { round, setRound, items, currentItem, completed, inProgress, accepted, pending, failed };
}

function useOfferHubFocusReset(focused: boolean, reset: () => void) {
  const [wasFocused, setWasFocused] = useState(focused);
  if (focused !== wasFocused) {
    setWasFocused(focused);
    if (focused) reset();
  }
}

function getOfferHubInteraction({
  tab,
  active,
  completed,
  hasCurrentItem,
  tutorial,
  replayingSwipeTutorial,
  swipeTutorialStep,
}: {
  tab: HubTab;
  active: boolean;
  completed: boolean;
  hasCurrentItem: boolean;
  tutorial: boolean;
  replayingSwipeTutorial: boolean;
  swipeTutorialStep: number;
}) {
  const swiping =
    tab === 'suggestions' && active && !completed && hasCurrentItem && !tutorial && !replayingSwipeTutorial;
  const swipeTutorialSwiping =
    tab === 'suggestions' && replayingSwipeTutorial && (swipeTutorialStep === 1 || swipeTutorialStep === 2);
  return { swiping, swipeTutorialSwiping };
}

function getRequestedHubTab({
  route,
  canUseSuggestions,
  canUseAI,
  canUseStories,
}: {
  route: ComponentProps<NavigationScreen<'offer-hub' | 'publish'>>['route'];
  canUseSuggestions: boolean;
  canUseAI: boolean;
  canUseStories: boolean;
}) {
  const requestedTab =
    route.params?.creationTab ?? ('tab' in (route.params ?? {}) ? (route.params as { tab?: HubTab }).tab : undefined);
  const validRequestedTab =
    requestedTab === 'manual' ||
    requestedTab === 'suggestions' ||
    (requestedTab === 'photos' && canUseAI) ||
    (requestedTab === 'stories' && canUseStories)
      ? requestedTab
      : undefined;
  const initialTab = validRequestedTab ?? (route.name === 'offer-hub' || canUseSuggestions ? 'suggestions' : 'manual');
  return { requestedTab, validRequestedTab, initialTab };
}

function useOfferHubTabs({
  route,
  canUseSuggestions,
  canUseAI,
  canUseStories,
  isUserPending,
}: {
  route: ComponentProps<NavigationScreen<'offer-hub' | 'publish'>>['route'];
  canUseSuggestions: boolean;
  canUseAI: boolean;
  canUseStories: boolean;
  isUserPending: boolean;
}) {
  const { requestedTab, validRequestedTab, initialTab } = getRequestedHubTab({
    route,
    canUseSuggestions,
    canUseAI,
    canUseStories,
  });
  const [tab, setTab] = useState<HubTab>(initialTab);
  const [visitedTabs, setVisitedTabs] = useState<HubTab[]>([initialTab]);
  const selectTab = (selected: HubTab) => {
    if (selected === tab) return;
    track(Event.ChangeTab, {
      page_name: 'offerHub',
      tab:
        selected === 'suggestions'
          ? 'suggested'
          : selected === 'photos'
            ? 'bulk'
            : selected === 'stories'
              ? 'stories'
              : 'new',
    });
    setTab(selected);
    setVisitedTabs((tabs) => (tabs.includes(selected) ? tabs : [...tabs, selected]));
  };
  const [lastRequest, setLastRequest] = useState({ params: route.params, validRequestedTab });
  if (route.params !== lastRequest.params || validRequestedTab !== lastRequest.validRequestedTab) {
    setLastRequest({ params: route.params, validRequestedTab });
    const requested = validRequestedTab ?? (requestedTab ? initialTab : undefined);
    if (requested && requested !== tab) {
      setTab(requested);
      setVisitedTabs((visited) => (visited.includes(requested) ? visited : [...visited, requested]));
    }
  }
  if (
    !isUserPending &&
    ((tab === 'suggestions' && !canUseSuggestions) ||
      (tab === 'photos' && !canUseAI) ||
      (tab === 'stories' && !canUseStories))
  ) {
    setTab('manual');
    setVisitedTabs((visited) => (visited.includes('manual') ? visited : [...visited, 'manual']));
  }
  return { tab, setTab, visitedTabs, selectTab };
}

function useInitializeHubRound({
  language,
  roundLanguage,
  setRoundLanguage,
  round,
  setRound,
  ready,
  session,
  candidates,
  decisions,
}: Pick<ReturnType<typeof useOfferHubRoundState>, 'round' | 'setRound'> &
  Pick<ReturnType<typeof useOfferHubData>, 'ready' | 'session' | 'candidates' | 'decisions'> & {
    language: Language;
    roundLanguage: string | undefined;
    setRoundLanguage: (language: Language) => void;
  }) {
  const { save } = session;
  useEffect(() => {
    if (
      (round && roundLanguage === language) ||
      !ready ||
      !session.ready ||
      candidates.isFetching ||
      candidates.isError ||
      !candidates.data
    )
      return;
    // The query is localized, but the visible/persisted round is a separate snapshot.
    const stored = round ?? session.round;
    const storedItems = stored ? remainingRoundItems(stored, decisions) : [];
    const seen = new Set(decisions.map((decision) => decision.suggestedListingId));
    const next =
      stored && (round || storedItems.length)
        ? refreshHubRound(stored, candidates.data.suggestions, decisions, candidates.data.answeredCount)
        : {
            id: uuid(),
            items: candidates.data.suggestions.filter((item) => !seen.has(item.id)).slice(0, 10),
            answeredCountBeforeRound: candidates.data.answeredCount,
          };
    let cancelled = false;
    void Promise.resolve().then(() => {
      if (cancelled) return;
      setRound(next);
      setRoundLanguage(language);
      void save({ round: next });
    });
    return () => {
      cancelled = true;
    };
  }, [
    candidates.data,
    candidates.isError,
    candidates.isFetching,
    decisions,
    language,
    roundLanguage,
    setRoundLanguage,
    ready,
    round,
    save,
    session.ready,
    session.round,
    setRound,
  ]);
}

function getHubPresentationState({
  tab,
  focused,
  tutorialVisible,
  replayingSwipeTutorial,
  loaded,
}: {
  tab: HubTab;
  focused: boolean;
  tutorialVisible: boolean;
  replayingSwipeTutorial: boolean;
  loaded: boolean;
}) {
  const helpTutorialVisible = tab === 'suggestions' && (tutorialVisible || replayingSwipeTutorial);
  return {
    helpTutorialVisible,
    tutorialOverlayVisible: tab === 'suggestions' && loaded && tutorialVisible,
    overviewActive: tab === 'suggestions' && focused && !helpTutorialVisible,
  };
}
